/**
 * What goes wrong reaching a file in Google Drive or OneDrive, in words a person can
 * act on.
 *
 * Its own module, with nothing imported, so the rules for turning a status code into a
 * sentence can be tested without loading the application's sign-in.
 */

/** A failure the person can act on: said in their words, with the help section beside it. */
export class ConnectorError extends Error {
  constructor(
    message: string,
    readonly hint?: string,
    /**
     * The file cannot be reached at the address kept for it — deleted, moved out of
     * reach, or the person is signed in as somebody else. Choosing it again is the
     * fix, so the screen offers exactly that rather than a retry that will fail.
     */
    readonly lost = false
  ) {
    super(message);
    this.name = "ConnectorError";
  }
}

/** Closing a sign-in window or a picker is a choice, not a failure, and is never shown as one. */
export class Cancelled extends Error {
  constructor() {
    super("Cancelled.");
    this.name = "Cancelled";
  }
}

export const GOOGLE_SHEET = "application/vnd.google-apps.spreadsheet";
export const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Where a Drive file's bytes are.
 *
 * A Google Sheet has no bytes of its own and is exported — as a workbook rather than a
 * CSV, because Drive's CSV export is the first sheet only, and a person who picked a
 * workbook with a September tab expects to be asked which tab. Anything else Drive
 * holds is downloaded as it was uploaded.
 */
export function googleDownloadUrl(fileId: string, mimeType: string): string {
  const id = encodeURIComponent(fileId);
  return mimeType === GOOGLE_SHEET
    ? `https://www.googleapis.com/drive/v3/files/${id}/export?mimeType=${encodeURIComponent(XLSX)}`
    : `https://www.googleapis.com/drive/v3/files/${id}?alt=media&supportsAllDrives=true`;
}

/** The name ingest reads, which for an exported Sheet has to say it is now a workbook. */
export function googleFilename(name: string, mimeType: string): string {
  return mimeType === GOOGLE_SHEET && !/\.xlsx$/i.test(name) ? `${name}.xlsx` : name;
}

/** A Google file that is not a spreadsheet at all: a Doc, a Slides deck, a folder. */
export function notASpreadsheet(mimeType: string): boolean {
  return mimeType.startsWith("application/vnd.google-apps.") && mimeType !== GOOGLE_SHEET;
}

/**
 * An item in OneDrive or SharePoint, asked for with a link its bytes can be fetched from.
 *
 * Not `/content`: that answers with a redirect, and a browser will not follow a
 * redirect on a request that carried an Authorization header across origins. The
 * download link Graph hands back is pre-authorised for a few minutes and is fetched
 * with no header at all.
 */
export function graphItemUrl(driveId: string, itemId: string): string {
  return `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}?select=id,name,size,file,@microsoft.graph.downloadUrl`;
}

/**
 * A refusal from Drive or Graph, as the person should hear it.
 *
 * 403 and 404 are the same answer on purpose. Both services say "not found" for a file
 * the signed-in account cannot see, so there is no telling a deleted file from one
 * shared with somebody else — and the fix is the same either way: choose it again.
 */
export function failureFor(service: string, status: number, name: string, reason?: string): ConnectorError {
  if (status === 401) return new ConnectorError(`${service} asked BroadPaper to sign in again.`, "Try again — a sign-in window may open for a moment.");
  if (reason === "exportSizeLimitExceeded") {
    return new ConnectorError(
      `“${name}” is too large for Google to hand over as a spreadsheet.`,
      "Google converts Sheets of up to 10 MB. In Google Sheets, use File → Download → Microsoft Excel and drop the file in instead."
    );
  }
  if (status === 403 || status === 404) {
    return new ConnectorError(
      `BroadPaper can't open “${name}” in ${service}.`,
      "It may have been deleted or moved somewhere you can't reach, or you may be signed in as somebody else. Choose the file again.",
      true
    );
  }
  if (status === 429 || status >= 500) return new ConnectorError(`${service} is busy and didn't hand the file over.`, "Wait a minute and try again. Nothing in BroadPaper has changed.");
  return new ConnectorError(`${service} wouldn't hand the file over.`, "Try again. If it keeps happening, choose the file again.");
}

/** The file is bigger than the plan reads, which is known before downloading it where the service says. */
export function tooLarge(name: string, size: string, allowed: string): ConnectorError {
  return new ConnectorError(`“${name}” is ${size}, and your plan takes files up to ${allowed}.`, "Nothing has been read. A copy with just the sheet you need, or a CSV, usually gets under it.");
}

export function popupBlocked(service: string): ConnectorError {
  return new ConnectorError(`Your browser stopped the ${service} sign-in window from opening.`, "Allow pop-ups for this site in your browser's address bar, then try again.");
}

/**
 * The next step needs a window, and this click has already opened one.
 *
 * A browser lets one click open one popup. Signing in to Microsoft for the first time
 * can need two — your account, then permission for OneDrive itself — and a second
 * window opened without a click of its own is blocked without a word. So the screen
 * asks for the second click, rather than failing in a way nobody could see.
 */
export class AnotherClick extends ConnectorError {
  constructor(service: string) {
    super(`${service} needs you to confirm one more step.`, `Press Continue and a ${service} window will open.`);
    this.name = "AnotherClick";
  }
}

export function unreachable(service: string): ConnectorError {
  return new ConnectorError(`BroadPaper couldn't reach ${service}.`, "Check your connection and try again — nothing you have done has been lost.");
}
